/**
 * CDP SDK Reco Plugin
 *
 * Serves the recommendation widget through the SAME loader the site already uses
 * for tracking — no second `<script>` tag. The reco placement manifest rides in
 * /v1/config (pre-fetched by the universal loader), so this needs ZERO extra
 * config fetch: the plugin reads `siteProfile.reco.placements`, hands them to the
 * `_widget.js` render bundle via `window.__cdpReco`, and re-injects on SPA nav.
 *
 * Because it runs inside the SDK it reuses the SDK's identity (anonymous_id) and
 * consent — recommendations render regardless (a read, not tracking), but the
 * impression/click beacons are gated on analytics consent, which the standalone
 * widget could not see.
 *
 * The standalone `<script src=".../v1/reco/_widget.js"> + <reco-block>` path is
 * untouched — this is purely additive for SDK sites.
 */

import { definePlugin } from '../base';
import type { CDPPlugin, CDPCoreAPI } from '../base';
import type { SiteProfile } from '../../core/types';

export interface RecoPluginConfig {
  /** Force on/off. Default: auto — active when the profile carries placements. */
  enabled?: boolean;
  /** Origin serving /v1/reco/* — defaults to the SDK apiHost. */
  recoHost?: string;
  /** Override the render bundle URL. */
  widgetSrc?: string;
}

interface RecoGlobal {
  key: string;
  host: string;
  aid: string;
  consent: boolean;
  managed: true;
  manifest: unknown[] | null;
}

declare global {
  interface Window {
    __cdpReco?: RecoGlobal;
    MonfriReco?: { inject: () => void };
  }
}

export function createRecoPlugin(config: RecoPluginConfig = {}): CDPPlugin {
  let core: CDPCoreAPI;
  let started = false;
  let widgetRequested = false;

  const recoHost = (): string => config.recoHost || core.getConfig()?.apiHost || '';

  /** Publish the current identity/consent/manifest for the render bundle to read. */
  function publish(): void {
    const profile = core.getSiteProfile() as SiteProfile;
    const cfg = core.getConfig();
    window.__cdpReco = {
      key: cfg?.writeKey || '',
      host: recoHost(),
      aid: core.getAnonymousId() || '',
      consent: core.hasConsent('analytics'),
      managed: true,
      manifest: profile.reco?.placements ?? null,
    };
  }

  /** Re-run injection for the current page (dedup-guarded inside the widget). */
  function inject(): void {
    publish();
    try { window.MonfriReco?.inject(); } catch { /* ignore */ }
  }

  /** Load the render bundle once; it calls MonfriReco.inject on load. */
  function ensureWidget(): void {
    if (widgetRequested) return;
    widgetRequested = true;
    publish();
    if (window.MonfriReco) { inject(); return; } // already present (e.g. a manual tag on the page)
    const s = document.createElement('script');
    s.async = true;
    s.src = config.widgetSrc || recoHost() + '/v1/reco/_widget.js';
    s.onload = () => inject();
    (document.head || document.documentElement).appendChild(s);
  }

  function start(): void {
    if (started) return;
    const profile = core.getSiteProfile() as SiteProfile;
    const has = !!profile.reco?.placements?.length;
    if (config.enabled === false) return;
    if (config.enabled !== true && !has) return; // auto: only when there's something to inject
    started = true;
    ensureWidget();
  }

  return definePlugin<RecoPluginConfig>({
    name: 'reco',
    version: '1.0.0',
    config,
    install(c: CDPCoreAPI) {
      core = c;
    },
    onInit() {
      // Picker mode: the dashboard opened this page with ?monfri_picker=1 (or the
      // operator hit "Navigate" mid-pick, which sets a sessionStorage flag so the
      // picker auto-resumes across a real page load). Load the render bundle NOW,
      // bypassing the placement gate — else a not-yet-configured page would have
      // nothing to run the picker.
      let pick = false;
      try {
        pick = /[?&]monfri_picker=1(?:&|$)/.test(location.search)
          || sessionStorage.getItem('monfri_reco_picker') === '1';
      } catch { /* storage blocked */ }
      if (pick) {
        ensureWidget();
        return;
      }
      // Decide once the freshest Site Profile (with reco.placements) is available.
      core.whenConfigReady().then(() => start()).catch(() => start());
    },
    onPageChange() {
      if (started) inject();
      else start(); // a placement may target this route even if the entry page had none
    },
  });
}
